import { Component } from '@theme/component';

/**
 * ORIVON collection page helpers (sections/orivon-collection-{seo,hero,jobs}.liquid, plan §2.6).
 *
 * <orivon-read-more-component>: clamps long copy (`ref="content"`) to a few lines with a fade (CSS `.is-clamped`)
 * and reveals a "Read more" / "Read less" toggle (`ref="toggle"`, label in `ref="label"`, `on:click="/toggle"`).
 * The two labels come from the element's data-more-label / data-less-label (locales/en.default.json,
 * orivon.collection.read_more / read_less), with English fallbacks.
 * The toggle stays hidden when the text fits (re-checked when the viewport width changes), and without JS the full
 * text shows. aria-expanded follows the state.
 * Theme editor: re-initialises through connectedCallback when the section re-renders (shopify:section:load).
 *
 * @typedef {object} ReadMoreRefs
 * @property {HTMLElement} content - The clamped text.
 * @property {HTMLButtonElement} toggle - The Read more / Read less button.
 * @property {HTMLElement} [label] - The button's text.
 *
 * @extends {Component<ReadMoreRefs>}
 */
export class OrivonReadMoreComponent extends Component {
  requiredRefs = ['content', 'toggle'];

  #width = 0;
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#measure();
    window.addEventListener('resize', this.#onResize, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#onResize);
    cancelAnimationFrame(this.#frame);
  }

  #onResize = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      if (window.innerWidth !== this.#width) this.#measure();
    });
  };

  /** Clamp unless the shopper opened the text; show the toggle only when the text overflows the clamp. */
  #measure() {
    const { content, toggle } = this.refs;
    if (!content || !toggle) return;
    this.#width = window.innerWidth;
    if (toggle.getAttribute('aria-expanded') === 'true') return;

    content.classList.add('is-clamped');
    const overflows = content.scrollHeight > content.clientHeight + 8;
    content.classList.toggle('is-clamped', overflows);
    toggle.hidden = !overflows;
  }

  toggle() {
    const { content, toggle, label } = this.refs;
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(open));
    content.classList.toggle('is-clamped', !open);
    if (label) {
      label.textContent = open ? this.dataset.lessLabel || 'Read less' : this.dataset.moreLabel || 'Read more';
    }
  }
}

if (!customElements.get('orivon-read-more-component')) {
  customElements.define('orivon-read-more-component', OrivonReadMoreComponent);
}

/**
 * <orivon-coll-focus-component> (sections/orivon-collection-hero.liquid colour key, orivon-collection-jobs.liquid
 * pills): a list that scrolls sideways (`ref="scroller"`) opens scrolled to its focus item (`[data-o-focus]`: the
 * pill of the collection being viewed, or the first bottle of this collection in the key) when that item starts
 * outside the visible part, so a phone shows "Glass care" / bottles 05–06 without a swipe. The browser clamps the
 * offset at the end of the list. No-op when the list does not overflow (wrapped rows from 1024px) or is RTL.
 * Horizontal offset of the list itself only; the page scroll is never touched.
 *
 * @typedef {object} FocusRefs
 * @property {HTMLElement} scroller - The sideways-scrolling list.
 *
 * @extends {Component<FocusRefs>}
 */
export class OrivonCollFocusComponent extends Component {
  requiredRefs = ['scroller'];

  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#frame = requestAnimationFrame(() => this.#focus());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    cancelAnimationFrame(this.#frame);
  }

  #focus() {
    const { scroller } = this.refs;
    const item = scroller?.querySelector('[data-o-focus]');
    if (!scroller || !(item instanceof HTMLElement)) return;
    if (scroller.scrollWidth <= scroller.clientWidth + 1) return;

    const style = getComputedStyle(scroller);
    if (style.direction === 'rtl') return;

    const box = scroller.getBoundingClientRect();
    const rect = item.getBoundingClientRect();
    if (rect.left >= box.left && rect.right <= box.right) return;

    const start = parseFloat(style.scrollPaddingInlineStart) || parseFloat(style.paddingInlineStart) || 0;
    scroller.scrollTo({ left: scroller.scrollLeft + rect.left - box.left - start, behavior: 'instant' });
  }
}

if (!customElements.get('orivon-coll-focus-component')) {
  customElements.define('orivon-coll-focus-component', OrivonCollFocusComponent);
}
